import { NumberFormatPipe } from '@myre/ui-primitive-core';

import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  type OnDestroy,
} from '@angular/core';
import { type ChartDataset, type ChartOptions } from 'chart.js';
import 'chartjs-plugin-zoom';
import { BaseChartDirective } from 'ng2-charts';
import { BehaviorSubject, combineLatest, type Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { LegendBase } from '../chart-legend/legend-base';
import { ChartType } from '../enums';
import { defaultDatasetBarStyle, defaultDatasetLineStyle } from '../helpers';
import { type Abscissa, type Ordinate } from '../types';

import { optionsBarLineChart } from './helpers';

export type BarOrLineChart = ChartType.Bar | ChartType.Line;

export type BarLine = {
  type: BarOrLineChart;
  label: string;
  // Data for each abscissa, the string is the key of the abscissa and the number is the value
  data: Record<string, number>;
  // Color of the data
  color: string;
  // Stack key for the bar chart. For example, if you want to stack two bars, they must have the same stack key.
  // If you don't want to stack the bars, don't set this property.
  stackKey?: string;
};

/**
 * Displays a bar chart with the option to include curves.
 * Bar data can be stacked.
 */
@Component({
  selector: 'myre-chart-bar-line',
  templateUrl: './chart-bar-line.component.html',
  styleUrls: ['./chart-bar-line.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BaseChartDirective, ChartLegendComponent, AsyncPipe],
})
export class ChartBarLineComponent
  extends LegendBase<BarOrLineChart>
  implements OnDestroy
{
  protected readonly _chartType: BarOrLineChart = ChartType.Bar;

  private readonly _numberFormatPipe = inject(NumberFormatPipe);

  @Input()
  public set dataToCompare(data: BarLine[]) {
    this._dataToCompare$.next(data);
  }

  @Input()
  public set abscissa(abscissa: Abscissa[]) {
    this._abscissa$.next(abscissa);
  }

  /**
   * Optional transformation of the ordinate value.
   */
  @Input()
  public set ordinate(ordinate: Ordinate) {
    this._ordinate$.next(ordinate);
  }

  @Input()
  public size: number | null = null;

  protected _data$: Observable<{
    datasets: ChartDataset<BarOrLineChart>[];
    labels: string[];
  }>;

  protected _options$: Observable<ChartOptions<BarOrLineChart>>;

  private readonly _dataToCompare$ = new BehaviorSubject<BarLine[]>(null);
  private readonly _abscissa$ = new BehaviorSubject<Abscissa[]>(null);
  private readonly _ordinate$ = new BehaviorSubject<Ordinate>({
    transform: v => this._numberFormatPipe.transform(+v),
  });

  constructor() {
    super();

    this._manageDataListener();
    this._manageOptionsListener();
  }

  public ngOnDestroy(): void {
    this._dataToCompare$.complete();
    this._abscissa$.complete();
    this._ordinate$.complete();
  }

  private _manageDataListener(): void {
    this._data$ = combineLatest([this._dataToCompare$, this._abscissa$]).pipe(
      filter(([data, abscissa]) => !!data?.length && !!abscissa?.length),
      map(([data, abscissa]) => ({
        labels: abscissa.map(a => a.label),
        datasets: this._transformDataToDatasets(data, abscissa),
      })),
    );
  }

  private _manageOptionsListener(): void {
    this._options$ = combineLatest([
      this._dataToCompare$,
      this._ordinate$,
    ]).pipe(
      filter(([data]) => !!data?.length),
      map(([data, ordinate]) =>
        optionsBarLineChart(
          !!data.filter(d => d.stackKey !== undefined).length,
          ordinate.transform,
        ),
      ),
    );
  }

  private _transformDataToDatasets(
    data: BarLine[],
    abscissa: Abscissa[],
  ): ChartDataset<BarOrLineChart>[] {
    return data.map<ChartDataset<BarOrLineChart>>((d, index) => ({
      type: d.type,
      label: d.label,
      data: abscissa.map(a => d.data[a.key]),
      // If the bar isn't stacked, we set the stack key with the unique index of the bar.
      stack: d.stackKey ?? index.toString(),
      ...((d.type === 'bar'
        ? defaultDatasetBarStyle(d.color)
        : defaultDatasetLineStyle(d.color)) as any),
    }));
  }
}
